import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Sparkles,
  Calendar,
  PlayCircle,
  Home,
  Music,
  Smile,
  Paintbrush,
  ArrowRight,
} from "lucide-react";

const YOUTUBE_URL = "https://www.youtube.com/@tantalizingart";

const navItems = [
  {
    label: "Home",
    to: "/",
    icon: Home,
    description: "Main page",
  },
  {
    label: "Birthday Paintings",
    to: "/birthday-meaning-paintings",
    icon: Calendar,
    description: "Custom birthday meaning art",
  },
  {
    label: "Music Visualizer",
    to: "/music-visualizer",
    icon: Music,
    description: "Turn sound into visuals",
  },
  {
    label: "Mirror of Cheer",
    to: "/mirror-of-cheer",
    icon: Smile,
    description: "A playful mood-lifting AI mirror",
  },
];

export default function SiteNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 18);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/82 backdrop-blur-xl border-b border-white/10 py-3 shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
            : "bg-gradient-to-b from-black/85 via-black/55 to-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-6">
          <div className="flex items-center justify-between gap-4">
            {/* Logo / Brand */}
            <Link
              to="/"
              className="group flex items-center gap-3 outline-none"
              aria-label="Go to Tantalizing.Art home page"
            >
              <div className="relative w-11 h-11 rounded-full overflow-hidden border border-white/10 bg-white/5 shadow-[0_0_24px_rgba(0,180,166,0.12)] group-hover:border-[#00B4A6]/50 transition-all duration-300">
                <img
                  src="/logo.png"
                  alt="Tantalizing.Art logo"
                  className="w-full h-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <Sparkles className="w-4 h-4 text-[#00B4A6] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>

              <div className="leading-tight">
                <span className="block text-white font-black text-lg md:text-xl tracking-tight">
                  Tantalizing.Art
                </span>
                <span className="hidden sm:block text-[11px] text-zinc-500 font-semibold tracking-widest uppercase">
                  Trust Your Energy
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.045] px-2 py-2 backdrop-blur-md">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);

                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={`group relative flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all duration-300 ${
                        active
                          ? "bg-[#00B4A6] text-white shadow-[0_0_22px_rgba(0,180,166,0.25)]"
                          : "text-zinc-400 hover:text-white hover:bg-white/8"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 transition-transform duration-300 ${
                          active
                            ? "text-white"
                            : "text-zinc-500 group-hover:text-[#00B4A6] group-hover:scale-110"
                        }`}
                      />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Desktop YouTube CTA */}
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2.5 px-5 py-3 rounded-full bg-gradient-to-r from-[#C79A43]/18 to-[#00B4A6]/18 hover:from-[#C79A43]/32 hover:to-[#00B4A6]/32 border border-[#C79A43]/25 hover:border-[#C79A43]/50 text-white text-sm font-black transition-all duration-300 hover:-translate-y-0.5"
              >
                <PlayCircle className="w-4 h-4 text-[#C79A43] group-hover:scale-110 transition-transform duration-300" />
                Daily Affirmations
                <ArrowRight className="w-4 h-4 opacity-60 group-hover:translate-x-0.5 transition-transform duration-300" />
              </a>
            </div>

            {/* Tablet Navigation */}
            <div className="hidden md:flex lg:hidden items-center gap-3">
              <Link
                to="/birthday-meaning-paintings"
                className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-black transition-all duration-300 ${
                  isActive("/birthday-meaning-paintings")
                    ? "bg-[#00B4A6] text-white"
                    : "bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10"
                }`}
              >
                <Calendar className="w-4 h-4" />
                Birthday Art
              </Link>

              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-black bg-[#C79A43]/12 border border-[#C79A43]/25 text-white hover:bg-[#C79A43]/22 transition-all duration-300"
              >
                <PlayCircle className="w-4 h-4 text-[#C79A43]" />
                YouTube
              </a>

              <button
                type="button"
                onClick={() => setIsOpen((previous) => !previous)}
                className="p-2.5 bg-white/5 border border-white/10 rounded-xl text-zinc-300 hover:text-white hover:bg-white/10 transition-colors focus:outline-none"
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              onClick={() => setIsOpen((previous) => !previous)}
              className="md:hidden p-2.5 bg-white/5 border border-white/10 rounded-xl text-zinc-300 hover:text-white hover:bg-white/10 transition-colors focus:outline-none"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile / Tablet Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="fixed inset-0 z-40 bg-black/96 backdrop-blur-3xl pt-28 px-5 md:px-8 lg:hidden"
          >
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-24 left-[-8rem] w-72 h-72 rounded-full bg-[#00B4A6]/12 blur-[90px]" />
              <div className="absolute bottom-[-8rem] right-[-8rem] w-80 h-80 rounded-full bg-[#C79A43]/12 blur-[100px]" />
            </div>

            <div className="relative z-10 max-w-2xl mx-auto flex flex-col gap-4">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.to);

                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.to}
                      className={`group flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                        active
                          ? "bg-[#00B4A6]/12 border-[#00B4A6]/35 text-white"
                          : "bg-white/[0.045] border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <div
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                          active
                            ? "bg-[#00B4A6] text-white"
                            : "bg-black/30 border border-white/10 text-[#00B4A6] group-hover:border-[#00B4A6]/40"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-1">
                        <span className="block font-black text-lg">
                          {item.label}
                        </span>
                        <span className="block text-sm text-zinc-500 mt-0.5">
                          {item.description}
                        </span>
                      </div>

                      <ArrowRight
                        className={`w-5 h-5 transition-transform duration-300 ${
                          active
                            ? "text-[#00B4A6]"
                            : "text-zinc-600 group-hover:text-white group-hover:translate-x-1"
                        }`}
                      />
                    </Link>
                  </motion.div>
                );
              })}

              <div className="h-px w-full bg-white/10 my-2" />

              <motion.a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22 }}
                className="group flex items-center gap-4 p-4 bg-gradient-to-r from-[#C79A43]/16 to-[#00B4A6]/16 border border-[#C79A43]/25 hover:border-[#C79A43]/50 text-white rounded-2xl transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-2xl bg-[#C79A43]/12 border border-[#C79A43]/25 flex items-center justify-center shrink-0">
                  <PlayCircle className="w-5 h-5 text-[#C79A43]" />
                </div>

                <div className="flex-1">
                  <span className="block font-black text-lg">
                    Daily Affirmations
                  </span>
                  <span className="block text-sm text-zinc-400 mt-0.5">
                    Watch on the official Tantalizing.Art YouTube page
                  </span>
                </div>

                <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
              </motion.a>
            </div>

            <div className="absolute bottom-8 left-0 w-full px-6 text-center">
              <div className="max-w-2xl mx-auto rounded-2xl border border-white/10 bg-white/[0.035] px-5 py-4">
                <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs font-mono tracking-widest uppercase">
                  <Paintbrush className="w-4 h-4 text-[#00B4A6]" />
                  Trust Your Energy
                  <Sparkles className="w-4 h-4 text-[#C79A43]" />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
